import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const { login } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password, rememberMe);
      navigate("/");
    } catch (error) {
      setErrorMsg("Error de validación, revise sus credenciales");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-6 relative overflow-hidden bg-[url('/assets/Patron-GJ.webp')] bg-cover bg-center bg-repeat">
      {/* Overlay gradient oscuro */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="w-full h-full absolute inset-0 bg-gradient-to-br from-[#18181b]/80 via-[#18181b]/70 to-[#18181b]/90 backdrop-blur-[2px]"></div>
      </div>

      <Card
        variant="elevated"
        className="w-full max-w-md shadow-2xl backdrop-blur-md bg-card/95 border-border/50 animate-fadeInUp relative overflow-hidden z-10"
      >
        {/* Shimmer effect - reduced animation */}
        <div
          className="hidden md:block absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full animate-[shimmer_3s_ease-in-out_infinite]"
          style={{ animationDelay: "0.5s" }}
        ></div>

        <CardHeader className="space-y-5 md:space-y-6 text-center pb-6 md:pb-8 pt-6 md:pt-8">
          <div className="flex shrink-0 items-center justify-center">
            <img
              src="/assets/grupo-jade-negro.svg"
              alt="Grupo Jade Academia"
              decoding="async"
              className="h-24 w-24 md:h-32 md:w-32"
            />
          </div>
          <div className="space-y-2 md:space-y-3">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-accent bg-clip-text text-transparent">
              Bienvenido
            </h1>
            <p className="text-sm md:text-base text-muted-foreground font-medium">
              Ingresa tus credenciales para continuar
            </p>
            <div className="flex items-center justify-center gap-1 pt-2">
              <div className="h-1 w-8 rounded-full bg-gradient-accent"></div>
              <div className="h-1 w-1 rounded-full bg-primary/50"></div>
              <div className="h-1 w-1 rounded-full bg-primary/50"></div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-6 md:pb-8 px-6 md:px-8">
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-sm font-semibold flex items-center gap-1.5"
              >
                Correo Electrónico
                <span className="text-destructive">*</span>
              </Label>
              <div className="relative group">
                <div className="hidden md:block absolute inset-0 bg-gradient-accent rounded-lg blur opacity-0 group-focus-within:opacity-30 transition-opacity duration-300 -z-10"></div>
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-all duration-200" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu.email@jade.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pl-11 h-12 text-base"
                  autoComplete="email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-sm font-semibold flex items-center gap-1.5"
              >
                Contraseña
                <span className="text-destructive">*</span>
              </Label>
              <div className="relative group">
                <div className="hidden md:block absolute inset-0 bg-gradient-accent rounded-lg blur opacity-0 group-focus-within:opacity-30 transition-opacity duration-300 -z-10"></div>
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-all duration-200" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pl-11 pr-11 h-12 text-base"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                  tabIndex={-1}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {errorMsg && (
              <div className="flex items-center justify-center mt-2 animate-fade-in">
                <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gradient-to-r from-[#fff0f0] via-[#fbeaec] to-[#fff0f0] border border-[#ef3f47]/30 shadow-sm text-sm font-medium text-[#d92d38]">
                  <svg className="h-4 w-4 text-[#ef3f47]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" /></svg>
                  {errorMsg}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) =>
                    setRememberMe(checked as boolean)
                  }
                  disabled={isLoading}
                  className="transition-all duration-200 data-[state=checked]:bg-primary border-2 hover:border-primary/50 h-5 w-5"
                />
                <Label
                  htmlFor="rememberMe"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none hover:text-primary transition-colors"
                >
                  Permanecer conectado
                </Label>
              </div>
            </div>
            <div className="pt-2">
              <Button
                type="submit"
                className="w-full min-h-[48px] h-12 md:h-14 text-base md:text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
                size="lg"
                disabled={isLoading}
              >
                {/* Simplified shimmer */}
                <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>

                {isLoading ? (
                  <span className="flex items-center gap-2 relative z-10">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Ingresando...
                  </span>
                ) : (
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Ingresar
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 transition-transform group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                )}
              </Button>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-border/50 text-center">
            <p className="text-xs md:text-sm text-muted-foreground font-medium">
              Grupo Jade Academia © {new Date().getFullYear()}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
